import { useEffect, useRef } from 'react';
import { remoteGetRoom } from '../utils/remoteApi';
import { loadRemoteOperatorSession } from '../components/RemoteOperatorModal';
import { showRemoteJoinNotification } from '../utils/showRemoteJoinNotification';

const NOTIFY_PREF_KEY = 'deckpack-remote-notify-pending';
const DEDUPE_KEY = 'deckpack-remote-join-dedupe';

function readOptIn() {
  try {
    return localStorage.getItem(NOTIFY_PREF_KEY) === '1';
  } catch {
    return false;
  }
}

function recentDuplicateNotify(roomId, len) {
  try {
    const raw = localStorage.getItem(DEDUPE_KEY);
    const j = raw ? JSON.parse(raw) : null;
    if (!j || j.r !== roomId || j.l !== len) return false;
    return Date.now() - j.t < 3000;
  } catch {
    return false;
  }
}

function markNotifyDedupe(roomId, len) {
  try {
    localStorage.setItem(DEDUPE_KEY, JSON.stringify({ r: roomId, l: len, t: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Polls operator room for pending joins and fires notifications when the Remote modal is closed.
 * Use from App (control panel) and PresenterView so alerts still run when only the notes window is open.
 */
export function useRemoteJoinAlerts({
  session,
  remoteModalOpenRef,
  onOpenRemoteModal,
  setPendingCount,
  joinAlertPrefEpoch,
}) {
  const joinNotifyRoomIdRef = useRef(null);
  const prevJoinNotifyPendingRef = useRef(0);

  useEffect(() => {
    const pollIntervalMs = () => (typeof document !== 'undefined' && document.hidden ? 5000 : 2000);

    let cancelled = false;
    let timerId = null;

    const maybeNotifyJoin = (roomId, len) => {
      if (cancelled) return;

      if (remoteModalOpenRef.current) {
        prevJoinNotifyPendingRef.current = len;
        return;
      }

      const optIn = readOptIn();
      const granted =
        typeof Notification !== 'undefined' && Notification.permission === 'granted';
      const prev = prevJoinNotifyPendingRef.current;
      const secure =
        typeof window !== 'undefined' && typeof window.isSecureContext === 'boolean'
          ? window.isSecureContext
          : true;
      const canUseNotifications = granted && secure;

      if (optIn && canUseNotifications && len > prev && len > 0) {
        if (!recentDuplicateNotify(roomId, len)) {
          markNotifyDedupe(roomId, len);
          const body =
            len === 1
              ? 'Someone is waiting to join — open Remote to allow.'
              : `${len} devices waiting — open Remote to allow.`;
          void showRemoteJoinNotification({
            body,
            onPageNotification: (n) => {
              n.onclick = () => {
                try {
                  n.close();
                } catch {
                  /* ignore */
                }
                window.focus();
                onOpenRemoteModal?.();
              };
            },
          });
        }
      } else if (optIn && granted && len > prev && len > 0 && !secure) {
        console.warn('Deck Pack: join alerts need HTTPS (secure context) to show notifications.');
      }

      if (optIn && granted) {
        prevJoinNotifyPendingRef.current = len;
      } else if (len < prev) {
        prevJoinNotifyPendingRef.current = len;
      }
    };

    const poll = async () => {
      const active = session ?? loadRemoteOperatorSession();
      if (!active?.roomId || !active?.operatorSecret) {
        joinNotifyRoomIdRef.current = null;
        prevJoinNotifyPendingRef.current = 0;
        setPendingCount?.(0);
        return;
      }

      if (joinNotifyRoomIdRef.current !== active.roomId) {
        joinNotifyRoomIdRef.current = active.roomId;
        prevJoinNotifyPendingRef.current = 0;
      }

      try {
        const data = await remoteGetRoom(active.roomId, active.operatorSecret);
        if (cancelled) return;
        const pendingArr = Array.isArray(data.pending) ? data.pending : [];
        let len = pendingArr.length;
        if (len === 0 && Array.isArray(data.clients)) {
          const n = data.clients.filter((c) => c.status === 'pending').length;
          if (n) len = n;
        }
        setPendingCount?.(len);
        maybeNotifyJoin(active.roomId, len);
      } catch {
        /* ignore */
      }
    };

    const onVisibilityOrFocus = () => {
      void poll();
    };

    const tick = async () => {
      if (cancelled) return;
      await poll();
      if (cancelled) return;
      timerId = window.setTimeout(tick, pollIntervalMs());
    };

    void tick();
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    return () => {
      cancelled = true;
      if (timerId != null) clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, [
    session,
    joinAlertPrefEpoch,
    remoteModalOpenRef,
    onOpenRemoteModal,
    setPendingCount,
  ]);
}
